import { useId, useState, type FormEvent, type ReactNode } from 'react';
import type {
  AdminBuyerTeamRequest,
  BuyerTeamMember,
  BuyerTeamRequest,
  TeamRole,
} from '../api/types';
import {
  accessEndIso,
  accessEndReady,
  defaultAccessEnd,
  type AccessEndValue,
} from '../lib/access-end';
import { AccessEndPicker } from './access-end-picker';
import { Button } from './button';
import { Input } from './input';
import { TeamRoleChips, TeamRolePicker } from './seller-vote';

/**
 * Approve / Decline for one waiting team ask. Approving adds the person to the
 * buyer's team, and a buyer's team never has ongoing access (owner rule
 * 2026-10-21): the admin picks how long it lasts (default 2 weeks) as they
 * approve. Shared by the Team requests card and the buyer detail's Team section.
 */
export function ApproveTeamRequest({
  disabled,
  cannotApprove = false,
  onApprove,
  onDecline,
}: {
  /** The ask is being decided. */
  disabled: boolean;
  /** The ask can't be approved (only Decline is offered). */
  cannotApprove?: boolean;
  onApprove: (expiresAt: string) => void;
  onDecline: () => void;
}) {
  const [end, setEnd] = useState<AccessEndValue>(defaultAccessEnd);
  const iso = accessEndIso(end);
  return (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
      {cannotApprove ? null : <AccessEndPicker value={end} onChange={setEnd} disabled={disabled} />}
      <span className="flex gap-2">
        <Button
          size="sm"
          disabled={disabled || cannotApprove || !iso || !accessEndReady(end)}
          onClick={() => iso && onApprove(iso)}
        >
          Approve
        </Button>
        <Button size="sm" variant="outline" disabled={disabled} onClick={onDecline}>
          Decline
        </Button>
      </span>
    </div>
  );
}

/** The member's status line, in plain words. */
function memberStatus(m: BuyerTeamMember, viewer: 'buyer' | 'admin'): string {
  if (!m.hasAccess) return 'Access removed by the intermediary.';
  if (!m.ndaSigned) return 'Invited — sees the teaser until they sign the NDA.';
  return viewer === 'buyer'
    ? 'Signed the NDA — sees what you see.'
    : 'Signed the NDA — sees what this buyer sees.';
}

function isAdminRequest(r: BuyerTeamRequest): r is AdminBuyerTeamRequest {
  return 'problem' in r;
}

/**
 * A buyer's NAMED team (migrations 0070 + 0075) — shared by the admin's buyer
 * detail and the buyer portal's "My team" page so both describe the team the
 * same way. Presentational: the page owns the mutations.
 *
 *   - The lead BUYER asks to add someone by email (any domain); every ask is
 *     sent to the intermediary, who approves or declines. Their asks show as
 *     "waiting" / "declined" and can be withdrawn. They remove members directly.
 *   - The ADMIN adds by email directly, and approves or declines the buyer's
 *     asks — with the reason when one cannot be approved.
 *   - Each person can carry team roles (Legal, Financial, ...): picked when asking
 *     or adding, shown as chips, and editable later (`onSetRoles`).
 */
export function BuyerTeamEditor({
  members,
  requests = [],
  viewer,
  canManage = true,
  adding = false,
  removingId = null,
  decidingId = null,
  settingRolesId = null,
  error,
  onAdd,
  onRemove,
  onSetRoles,
  onWithdraw,
  onApprove,
  onDecline,
  rolesAside,
  sendAside,
}: {
  members: readonly BuyerTeamMember[];
  /** The buyer's asks (buyer view) or the waiting asks for this buyer (admin view). */
  requests?: readonly BuyerTeamRequest[];
  /** `buyer`: the lead buyer's own page ("your team"); `admin`: a buyer's team. */
  viewer: 'buyer' | 'admin';
  canManage?: boolean;
  adding?: boolean;
  removingId?: string | null;
  /** The request being withdrawn / approved / declined. */
  decidingId?: string | null;
  /** The member whose roles are being saved. */
  settingRolesId?: string | null;
  error?: string | null;
  /** Add (admin, with the date their access ends) or ask (buyer, no date) by email,
   * with the roles picked for them. A returned promise that resolves clears the form. */
  onAdd: (email: string, roles: TeamRole[], expiresAt?: string) => Promise<unknown> | void;
  onRemove: (memberId: string) => void;
  /** Change a member's roles (an empty list clears them). Omit to hide "Edit roles". */
  onSetRoles?: (memberId: string, roles: TeamRole[]) => void;
  onWithdraw?: (requestId: string) => void;
  /** Approve a waiting ask, with the date the new member's access ends. */
  onApprove?: (requestId: string, expiresAt: string) => void;
  onDecline?: (requestId: string) => void;
  /** A "?" help tip shown after the "Roles (optional)" legend of the add form. */
  rolesAside?: ReactNode;
  /** A "?" help tip shown after the add form's submit button. */
  sendAside?: ReactNode;
}) {
  const [email, setEmail] = useState('');
  // When an ADMIN-added person's access ends: required, never ongoing.
  const [end, setEnd] = useState<AccessEndValue>(defaultAccessEnd);
  const [sent, setSent] = useState(false);
  const [roles, setRoles] = useState<TeamRole[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);
  // The member whose roles are open for editing, and what is ticked meanwhile.
  const [editing, setEditing] = useState<{ id: string; roles: TeamRole[] } | null>(null);
  const inputId = useId();
  const trimmed = email.trim();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!trimmed || adding) return;
    const expiresAt = viewer === 'admin' ? accessEndIso(end) : undefined;
    if (viewer === 'admin' && !expiresAt) return;
    setSent(false);
    const result = onAdd(trimmed, roles, expiresAt ?? undefined);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<unknown>).then(
        () => {
          setEmail('');
          setRoles([]);
          setSent(viewer === 'buyer');
        },
        () => undefined, // the page shows the error; keep what was typed
      );
    }
  }

  return (
    <div className="space-y-3" data-testid="buyer-team">
      {members.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {viewer === 'buyer'
            ? 'No one yet. Add the people who work on this deal with you.'
            : 'This buyer has not named a team.'}
        </p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label="Team members">
          {members.map((m) => (
            <li key={m.actorId} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="min-w-0 space-y-0.5">
                <p className="break-all text-sm font-medium">
                  {m.displayName ? `${m.displayName} (${m.email})` : m.email}{' '}
                  <TeamRoleChips roles={m.roles} className="align-middle" />
                </p>
                <p className="text-xs text-muted-foreground">
                  {memberStatus(m, viewer)}
                  {m.addedByRole === 'admin' ? ' Added by the intermediary.' : ''}
                </p>
              </div>
              {canManage ? (
                confirming === m.actorId ? (
                  <span className="flex items-center gap-2">
                    <span className="text-xs">Remove {m.email}?</span>
                    <Button
                      size="sm"
                      variant="danger-ghost"
                      disabled={removingId === m.actorId}
                      onClick={() => {
                        onRemove(m.actorId);
                        setConfirming(null);
                      }}
                    >
                      {removingId === m.actorId ? 'Removing…' : 'Remove'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {onSetRoles ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={settingRolesId === m.actorId}
                        onClick={() => setEditing({ id: m.actorId, roles: [...(m.roles ?? [])] })}
                      >
                        Edit roles
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={removingId === m.actorId}
                      onClick={() => setConfirming(m.actorId)}
                    >
                      Remove
                    </Button>
                  </span>
                )
              ) : null}
              {editing?.id === m.actorId && onSetRoles ? (
                <div className="w-full space-y-2" data-testid="edit-roles">
                  <TeamRolePicker
                    value={editing.roles}
                    onChange={(next) => setEditing({ id: m.actorId, roles: next })}
                    legend={`Roles for ${m.email}`}
                    legalNote={false}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        onSetRoles(m.actorId, editing.roles);
                        setEditing(null);
                      }}
                    >
                      Save roles
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {requests.length > 0 ? (
        <ul className="divide-y rounded-md border" aria-label="Team requests">
          {requests.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="min-w-0 space-y-0.5">
                <p className="break-all text-sm font-medium">
                  {r.email} <TeamRoleChips roles={r.roles} className="align-middle" />
                </p>
                <p className="text-xs text-muted-foreground">
                  {viewer === 'buyer'
                    ? r.status === 'declined'
                      ? 'Declined by the intermediary.'
                      : 'Waiting for the intermediary to approve.'
                    : 'The buyer asked to add this person.'}
                </p>
                {isAdminRequest(r) && r.problem ? (
                  <p className="text-xs text-destructive">{r.problem}</p>
                ) : isAdminRequest(r) && r.existingAccount ? (
                  <p className="text-xs text-muted-foreground">
                    Existing account — approving re-invites them.
                  </p>
                ) : null}
              </div>
              {viewer === 'buyer' && canManage && onWithdraw ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decidingId === r.id}
                  onClick={() => onWithdraw(r.id)}
                >
                  {r.status === 'declined' ? 'Clear' : 'Withdraw'}
                </Button>
              ) : null}
              {viewer === 'admin' && onApprove && onDecline ? (
                <ApproveTeamRequest
                  disabled={decidingId === r.id}
                  cannotApprove={isAdminRequest(r) && r.problem !== null}
                  onApprove={(expiresAt) => onApprove(r.id, expiresAt)}
                  onDecline={() => onDecline(r.id)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {canManage ? (
        <form className="space-y-1.5" onSubmit={submit}>
          <label htmlFor={inputId} className="text-sm font-medium">
            {viewer === 'buyer' ? 'Ask to add a person by email' : 'Add a person by email'}
          </label>
          <div className="flex flex-wrap gap-2">
            <Input
              id={inputId}
              type="email"
              autoComplete="off"
              placeholder="name@firm.com"
              value={email}
              disabled={adding}
              onChange={(e) => {
                setEmail(e.target.value);
                setSent(false);
              }}
              className="min-w-[12rem] flex-1"
            />
            <div className="flex items-center gap-1.5">
              <Button
                type="submit"
                disabled={adding || trimmed === '' || (viewer === 'admin' && !accessEndReady(end))}
              >
                {adding
                  ? 'Sending…'
                  : viewer === 'buyer'
                    ? 'Send to the intermediary'
                    : 'Add to team'}
              </Button>
              {sendAside}
            </div>
          </div>
          <TeamRolePicker
            value={roles}
            onChange={setRoles}
            disabled={adding}
            legend="Roles (optional)"
            legendAside={rolesAside}
            legalNote={false}
            className="pt-1"
          />
          {viewer === 'admin' ? (
            <AccessEndPicker value={end} onChange={setEnd} disabled={adding} />
          ) : null}
          {sent ? (
            <p role="status" className="text-sm">
              Sent to the intermediary. They approve each person before they are invited.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Any email address works — colleagues and outside advisers such as your accountant,
            lawyer or lender.{' '}
            {viewer === 'buyer'
              ? 'The intermediary approves each person; then they get an invitation, sign the NDA, and see what you see. If your LOI is accepted, the team keeps access while other buyers are paused.'
              : 'They get an invitation, sign the NDA, and then see what this buyer sees. If an LOI from this buyer is accepted, the team keeps access while other buyers are paused.'}
          </p>
        </form>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
